<template>
  <div class="modal fade" id="new-business" tabindex="-1" role="dialog" aria-labelledby="exampleModalCenterTitle" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title text-capitalize" id="exampleModalLongTitle">New SBP Application</h5>
          <button type="button" class="btn-close" data-dismiss="modal" aria-label="Close"></button>
        </div>

        <div v-if="message !== null" class="alert alert-danger mx-3 mt-3 mb-0" role="alert">
          {{ message }}
        </div>

        <!-- Step Progress Indicator -->
        <div class="step-indicator px-3 pt-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div
                v-for="(stepName, index) in stepNames"
                :key="index"
                class="step-item text-center flex-fill"
                :class="{
                  'active': currentStep === index,
                  'completed': currentStep > index
                }"
            >
              <div
                  class="step-circle mx-auto mb-1"
                  :class="{
                    'bg-primary text-white': currentStep === index,
                    'bg-success text-white': currentStep > index,
                    'bg-light text-muted border': currentStep < index
                  }"
                  style="width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: bold;"
              >
                <i v-if="currentStep > index" class="mdi mdi-check"></i>
                <span v-else>{{ index + 1 }}</span>
              </div>
              <small
                  class="d-none d-md-block"
                  :class="{
                    'text-primary fw-bold': currentStep === index,
                    'text-success': currentStep > index,
                    'text-muted': currentStep < index
                  }"
                  style="font-size: 11px;"
              >
                {{ stepName }}
              </small>
            </div>
          </div>
          <div class="progress" style="height: 4px;">
            <div
                class="progress-bar bg-success"
                role="progressbar"
                :style="{ width: ((currentStep / (stepNames.length - 1)) * 100) + '%' }"
            ></div>
          </div>
        </div>

        <div class="modal-body steps-container">
          <!-- Step 0: Year Selection (Mandatory First Step) -->
          <div class="row the-step year-selection active">
            <div class="col-12">
              <div class="bg-warning border-2 bg-soft p-4 mb-4 rounded">
                <div class="d-flex align-items-center mb-3">
                  <i class="mdi mdi-calendar-clock text-warning me-2" style="font-size: 32px;"></i>
                  <div>
                    <h5 class="fw-bold mb-1 text-dark">Select Permit Year</h5>
                    <p class="text-muted mb-0">This determines the validity period of your Single Business Permit</p>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label fw-semibold">
                        Permit Year <span class="text-danger fw-bold">*</span>
                      </label>
                      <select
                          v-model="localTrade.fiscalYear"
                          class="form-control form-control-lg bg-white border-2"
                          @change="updatePermitDates"
                      >
                        <option value="">-- Select Year --</option>
                        <option v-for="(item, index) in years" :value="item.year" :key="index">
                          {{ item.year }}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div v-if="localTrade.fiscalYear" class="permit-validity-card p-3 bg-white rounded border border-success">
                      <div class="d-flex align-items-center mb-2">
                        <i class="mdi mdi-check-circle text-success me-2" style="font-size: 24px;"></i>
                        <span class="fw-semibold text-success">Permit Validity Period</span>
                      </div>
                      <div class="row">
                        <div class="col-6">
                          <small class="text-muted d-block">Start Date</small>
                          <strong class="text-dark">{{ permitStartDate }}</strong>
                        </div>
                        <div class="col-6">
                          <small class="text-muted d-block">Expiry Date</small>
                          <strong class="text-danger">{{ permitEndDate }}</strong>
                        </div>
                      </div>
                    </div>

                    <div v-else class="permit-validity-card p-3 bg-light rounded border border-secondary">
                      <div class="d-flex align-items-center mb-2">
                        <i class="mdi mdi-information-outline text-secondary me-2" style="font-size: 24px;"></i>
                        <span class="fw-semibold text-secondary">Permit Validity Period</span>
                      </div>
                      <p class="text-muted mb-0 small">
                        <i class="mdi mdi-arrow-left me-1"></i>
                        Select a year to see permit validity dates
                      </p>
                    </div>
                  </div>
                </div>

                <div class="mt-3 p-3 bg-white rounded border">
                  <h6 class="fw-semibold mb-2">
                    <i class="mdi mdi-help-circle-outline text-info me-1"></i>
                    Which year should I select?
                  </h6>
                  <div class="row">
                    <div class="col-md-6">
                      <div class="d-flex align-items-start mb-2">
                        <span class="badge bg-primary me-2">{{ currentYear }}</span>
                        <div>
                          <strong>Current Year</strong>
                          <p class="text-muted small mb-0">
                            Select this if you need a permit for the current operating year.
                            <br><em>Example: Permit valid from 1st January {{ currentYear }} to 31st December {{ currentYear }}</em>
                          </p>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="d-flex align-items-start">
                        <span class="badge bg-success me-2">{{ nextYear }}</span>
                        <div>
                          <strong>Next Year</strong>
                          <p class="text-muted small mb-0">
                            Select this if you are applying in advance for next year's permit.
                            <br><em>Example: Permit valid from 1st January {{ nextYear }} to 31st December {{ nextYear }}</em>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="!localTrade.fiscalYear" class="alert alert-warning mt-3 mb-0 d-flex align-items-center">
                  <i class="mdi mdi-alert-circle-outline me-2" style="font-size: 20px;"></i>
                  <span>
                    <strong>Required:</strong> You must select a permit year before proceeding with the application.
                  </span>
                </div>

                <div v-if="localTrade.fiscalYear" class="mt-3 p-3 bg-white rounded border border-primary">
                  <div class="form-check">
                    <input
                        v-model="yearConfirmed"
                        class="form-check-input"
                        type="checkbox"
                        id="confirmYearSelection"
                        style="width: 20px; height: 20px; cursor: pointer;"
                    >
                    <label class="form-check-label ms-2" for="confirmYearSelection" style="cursor: pointer;">
                      <strong class="text-dark">I confirm and understand that:</strong>
                      <ul class="mb-0 mt-2 text-muted">
                        <li>The Single Business Permit for <strong class="text-primary">{{ localTrade.fiscalYear }}</strong> will be valid from <strong class="text-success">{{ permitStartDate }}</strong></li>
                        <li>The permit will <strong class="text-danger">expire on {{ permitEndDate }}</strong></li>
                        <li>I will need to renew the permit before it expires to continue operating legally</li>
                      </ul>
                    </label>
                  </div>

                  <div v-if="yearConfirmed" class="alert alert-success mt-3 mb-0 d-flex align-items-center">
                    <i class="mdi mdi-check-circle me-2" style="font-size: 20px;"></i>
                    <span><strong>Confirmed!</strong> You may now proceed to the next step.</span>
                  </div>
                  <div v-else class="alert alert-info mt-3 mb-0 d-flex align-items-center">
                    <i class="mdi mdi-checkbox-marked-outline me-2" style="font-size: 20px;"></i>
                    <span>Please check the box above to confirm you understand the permit validity period.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 1: Business Owner Info -->
          <div class="row the-step biz-info d-none">
            <div class="col-12">
              <div class="bg-primary border-2 bg-soft p-3 mb-4">
                <p class="fw-semibold mb-0 pb-0">Business Owner</p>
              </div>
            </div>

            <div class="col-8">
              <div class="mb-3">
                <label class="form-label">Full Name <span class="text-danger fw-bold">*</span></label>
                <input v-model="localTrade.businessDetails.fullNames" type="text" class="form-control" placeholder="Enter Owner's Full Names">
              </div>
            </div>

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">Owner's ID Number <span class="text-danger fw-bold">*</span></label>
                <input v-model="localTrade.businessDetails.ownerID" type="text" class="form-control" placeholder="Enter Owner's ID Number">
              </div>
            </div>

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">Owner's Phone No. <span class="text-danger fw-bold">*</span></label>
                <input v-model="localTrade.businessDetails.ownerPhone" type="text" class="form-control" placeholder="Enter Owner's Phone No.">
              </div>
            </div>

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">Email</label>
                <input v-model="localTrade.businessDetails.ownerEmail" type="email" class="form-control" placeholder="Owner's Email address">
              </div>
            </div>

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">KRA Pin</label>
                <input v-model="localTrade.businessDetails.kraPin" type="text" class="form-control" placeholder="Owner's KRA Pin">
              </div>
            </div>
          </div>

          <!-- Step 2: Business Details with MANDATORY LOCATION -->
          <div class="row the-step biz-additional-info d-none">
            <div class="col-12">
              <div class="bg-primary border-2 bg-soft p-3 mb-4">
                <p class="fw-semibold mb-0 pb-0">Business Details</p>
              </div>
            </div>

            <div class="col-12">
              <div class="mb-3">
                <label class="form-label">Business Name <span class="text-danger fw-bold">*</span></label>
                <input v-model="localTrade.businessDetails.businessName" type="text" class="form-control" :class="{'is-invalid': businessNameError}" placeholder="Enter Business Name">
                <div v-if="businessNameError" class="invalid-feedback">
                  Business Name is required
                </div>
              </div>
            </div>

            <!-- ==================== MANDATORY LOCATION SECTION ==================== -->
            <div class="col-12">
              <!-- Location Requirement Banner -->
              <div class="alert mb-3" :class="locationCaptured ? 'alert-success' : 'alert-danger'">
                <div class="d-flex align-items-center">
                  <i class="mdi me-2" :class="locationCaptured ? 'mdi-check-circle text-success' : 'mdi-map-marker-alert text-danger'" style="font-size: 28px;"></i>
                  <div>
                    <strong v-if="!locationCaptured" class="text-danger">⚠️ Business Location Required</strong>
                    <strong v-else class="text-success">✓ Location Captured Successfully</strong>
                    <p class="mb-0 small" v-if="!locationCaptured">
                      You <strong>must</strong> provide the business location coordinates to proceed. Check the box below to use your current GPS location, or click on the map to select the business location.
                    </p>
                    <p class="mb-0 small" v-else>
                      Business location has been captured. You can proceed to the next step.
                    </p>
                  </div>
                </div>
              </div>

              <!-- GPS Location Checkbox -->
              <div class="mb-3 p-3 border rounded" :class="useCurrentLocation ? 'border-success bg-success-soft' : 'border-primary'">
                <div class="form-check">
                  <input
                      v-model="useCurrentLocation"
                      @change="handleUseCurrentLocation"
                      class="form-check-input"
                      type="checkbox"
                      id="useCurrentLocation"
                      style="width: 22px; height: 22px; cursor: pointer;"
                  >
                  <label class="form-check-label ms-2" for="useCurrentLocation" style="cursor: pointer;">
                    <i class="mdi mdi-crosshairs-gps me-1 text-primary" style="font-size: 20px;"></i>
                    <strong>I am at the business location</strong>
                    <span class="text-muted">(Use my current GPS coordinates)</span>
                  </label>
                </div>

                <!-- Location Status Messages -->
                <div class="mt-2 ms-4">
                  <div v-if="gettingLocation" class="d-flex align-items-center text-primary py-2">
                    <i class="mdi mdi-loading mdi-spin me-2" style="font-size: 24px;"></i>
                    <span><strong>Getting your location...</strong> Please wait and allow location access if prompted.</span>
                  </div>

                  <div v-if="locationError" class="alert alert-danger mt-2 mb-0 py-2">
                    <div class="d-flex align-items-start">
                      <i class="mdi mdi-alert-circle me-2" style="font-size: 20px;"></i>
                      <div>
                        <strong>Location Error:</strong> {{ locationError }}
                        <div class="mt-2">
                          <small class="text-muted">
                            <strong>Troubleshooting:</strong>
                            <ul class="mb-0 ps-3 mt-1">
                              <li>Ensure location services are enabled on your device</li>
                              <li>Allow this website to access your location in browser settings</li>
                              <li>Alternatively, click directly on the map below to set the location</li>
                            </ul>
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="locationSuccess && !gettingLocation" class="alert alert-success mt-2 mb-0 py-2">
                    <i class="mdi mdi-check-circle me-1"></i>
                    <strong>GPS location captured successfully!</strong> Coordinates have been saved.
                  </div>
                </div>
              </div>

              <!-- Coordinates & Address Display Card -->
              <div v-if="locationCaptured" class="mb-3 p-3 rounded border border-success" style="background-color: rgba(40, 167, 69, 0.1);">
                <div class="d-flex align-items-start justify-content-between">
                  <div class="flex-grow-1">
                    <div class="d-flex align-items-center mb-2">
                      <i class="mdi mdi-map-marker-check text-success me-2" style="font-size: 28px;"></i>
                      <strong class="text-success">Business Location</strong>
                    </div>

                    <!-- Address Display -->
                    <div v-if="locationAddress" class="mb-3 p-2 bg-white rounded">
                      <small class="text-muted d-block mb-1">
                        <i class="mdi mdi-home-map-marker me-1"></i>Address
                      </small>
                      <strong class="text-dark">{{ locationAddress }}</strong>
                    </div>
                    <div v-else-if="fetchingAddress" class="mb-3 p-2 bg-white rounded">
                      <small class="text-muted">
                        <i class="mdi mdi-loading mdi-spin me-1"></i>Fetching address...
                      </small>
                    </div>

                    <!-- Coordinates -->
                    <div class="row">
                      <div class="col-6">
                        <small class="text-muted d-block">Latitude</small>
                        <strong class="text-dark" style="font-size: 16px;">{{ localTrade.businessDetails.lat.toFixed(6) }}</strong>
                      </div>
                      <div class="col-6">
                        <small class="text-muted d-block">Longitude</small>
                        <strong class="text-dark" style="font-size: 16px;">{{ localTrade.businessDetails.lng.toFixed(6) }}</strong>
                      </div>
                    </div>
                  </div>
                  <button
                      type="button"
                      class="btn btn-outline-danger btn-sm ms-2"
                      @click="clearLocation"
                      title="Clear location and select again"
                  >
                    <i class="mdi mdi-close"></i> Clear
                  </button>
                </div>
              </div>

              <!-- Search Location Input -->
              <div class="mb-3 position-relative">
                <label class="form-label fw-semibold">
                  <i class="mdi mdi-magnify me-1"></i>
                  Search for Location
                </label>
                <div class="input-group">
                  <span class="input-group-text bg-white">
                    <i v-if="searching" class="mdi mdi-loading mdi-spin text-primary"></i>
                    <i v-else class="mdi mdi-map-search text-muted"></i>
                  </span>
                  <input
                      v-model="searchQuery"
                      @input="debouncedSearch"
                      @keydown.down.prevent="navigateResults('down')"
                      @keydown.up.prevent="navigateResults('up')"
                      @keydown.enter.prevent="selectHighlightedResult"
                      @keydown.escape="clearSearch"
                      @focus="showResults = true"
                      type="text"
                      class="form-control form-control-lg"
                      placeholder="Start typing to search (e.g., Westlands, Moi Avenue, Kenyatta...)"
                      autocomplete="off"
                  >
                  <button
                      v-if="searchQuery"
                      class="btn btn-outline-secondary"
                      type="button"
                      @click="clearSearch"
                      title="Clear search"
                  >
                    <i class="mdi mdi-close"></i>
                  </button>
                </div>

                <!-- Search Results Dropdown -->
                <div v-if="showResults && (searchResults.length > 0 || searching || (searchQuery.length >= 2 && searchResults.length === 0 && !searching))"
                     class="search-results-dropdown border rounded shadow-sm">

                  <!-- Loading State -->
                  <div v-if="searching" class="p-3 text-center text-muted">
                    <i class="mdi mdi-loading mdi-spin me-2"></i>
                    Searching for "{{ searchQuery }}"...
                  </div>

                  <!-- Results List -->
                  <div v-else-if="searchResults.length > 0">
                    <div
                        v-for="(result, index) in searchResults"
                        :key="index"
                        class="search-result-item p-2 border-bottom d-flex align-items-start"
                        :class="{ 'bg-primary bg-opacity-10': highlightedIndex === index }"
                        @click="selectSearchResult(result)"
                        @mouseenter="highlightedIndex = index"
                    >
                      <i class="mdi mdi-map-marker text-danger me-2 mt-1" style="font-size: 18px;"></i>
                      <div class="flex-grow-1">
                        <div class="fw-semibold text-dark">{{ getPlaceName(result) }}</div>
                        <small class="text-muted">{{ getPlaceDetails(result) }}</small>
                      </div>
                      <i class="mdi mdi-chevron-right text-muted"></i>
                    </div>
                  </div>

                  <!-- No Results -->
                  <div v-else-if="searchQuery.length >= 2 && !searching" class="p-3 text-center text-muted">
                    <i class="mdi mdi-map-marker-off me-2"></i>
                    No locations found for "{{ searchQuery }}"
                    <div class="mt-2">
                      <small>Try a different search term or click on the map</small>
                    </div>
                  </div>

                  <!-- Footer -->
                  <div v-if="searchResults.length > 0" class="p-2 bg-light border-top d-flex justify-content-between align-items-center">
                    <small class="text-muted">
                      <i class="mdi mdi-keyboard me-1"></i>
                      Use ↑↓ to navigate, Enter to select
                    </small>
                    <small class="text-muted">
                      Powered by OpenStreetMap
                    </small>
                  </div>
                </div>

                <small class="text-muted mt-1 d-block">
                  <i class="mdi mdi-information-outline me-1"></i>
                  Type at least 2 characters to search. Results are filtered to Kenya.
                </small>
              </div>

              <!-- Map Container -->
              <div class="mb-3">
                <label class="form-label fw-semibold">
                  <i class="mdi mdi-map me-1"></i>
                  Select Location on Map <span class="text-danger fw-bold">*</span>
                </label>
                <small class="d-block text-muted mb-2">
                  <i class="mdi mdi-information-outline me-1"></i>
                  Click anywhere on the map or drag the marker to set the exact business location
                </small>

                <div style="height: 400px; position: relative;">
                  <div v-if="mapInitializing"
                       style="position: absolute; top: 0; left: 0; right: 0; bottom: 0;
                              background: rgba(255,255,255,0.9); z-index: 1000;
                              display: flex; align-items: center; justify-content: center;
                              border: 1px solid #ddd; border-radius: 4px;">
                    <div class="text-center">
                      <i class="mdi mdi-loading mdi-spin" style="font-size: 48px; color: #007bff;"></i>
                      <p class="mt-2">Loading map...</p>
                    </div>
                  </div>
                  <div id="leafletMap" class="w-100 h-100 rounded border"></div>
                </div>
                <small class="text-muted mt-1 d-block">
                  <i class="mdi mdi-information me-1"></i>
                  Map powered by OpenStreetMap - Free and open source
                </small>
              </div>
            </div>
            <!-- ==================== END MANDATORY LOCATION SECTION ==================== -->

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">Sub County</label>
                <select v-model="localSubCounty" class="form-control bg-white" title="Select Sub County">
                  <option v-for="(item, index) in subCounties" :value="item" :key="index">{{ item.subCountyName }}</option>
                </select>
              </div>
            </div>

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">Ward</label>
                <select v-model="localWard" class="form-control bg-white" title="Select ward">
                  <option v-for="(item, index) in wards" :value="item" :key="index">{{ item.wardName }}</option>
                </select>
              </div>
            </div>

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">Plot No.</label>
                <input v-model="localTrade.businessDetails.plotNumber" type="text" class="form-control" placeholder="Enter Plot No.">
              </div>
            </div>

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">Business Premises Nature</label>
                <select v-model="localTrade.businessDetails.buildingOccupancy" class="form-control bg-white">
                  <option value="Occupies Section In building">Occupies Section In building</option>
                  <option value="Occupies Entire Building">Occupies Entire Building</option>
                  <option value="Mobile Business">Mobile Business</option>
                </select>
              </div>
            </div>

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">Floor Number</label>
                <select v-model="localTrade.businessDetails.floorNo" class="form-control bg-white">
                  <option v-for="(item, index) in floors" :value="item.floor" :key="index">{{ item.floor }}</option>
                </select>
              </div>
            </div>

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">Room Number</label>
                <input v-model="localTrade.businessDetails.roomNo" type="text" class="form-control" placeholder="Room No">
              </div>
            </div>
          </div>

          <!-- Step 3: Contact Information -->
          <div class="row the-step biz-owner d-none">
            <div class="col-12">
              <div class="bg-primary border-2 bg-soft p-3 mb-4">
                <p class="fw-semibold mb-0 pb-0">Business contact information</p>
              </div>
            </div>

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">Company's Email address</label>
                <input v-model="localTrade.businessDetails.businessEmail" type="text" class="form-control" placeholder="Enter email">
              </div>
            </div>

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">Postal address <span class="text-danger fw-bold">*</span></label>
                <input v-model="localTrade.businessDetails.postalAddress" type="text" class="form-control" placeholder="Enter Postal address *">
              </div>
            </div>

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">Postal Code <span class="text-danger fw-bold">*</span></label>
                <input v-model="localTrade.businessDetails.postalCode" type="text" class="form-control" placeholder="Postal Code">
              </div>
            </div>

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">Company's Phone No. <span class="text-danger fw-bold">*</span></label>
                <input v-model="localTrade.businessDetails.businessPhone" type="text" class="form-control" placeholder="Enter Company's Phone No.">
              </div>
            </div>

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">Physical Address <span class="text-danger fw-bold">*</span></label>
                <input v-model="localTrade.businessDetails.physicalAddress" type="text" class="form-control" placeholder="Enter physical Address">
              </div>
            </div>

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">Building Name <span class="text-danger fw-bold">*</span></label>
                <input v-model="localTrade.businessDetails.buildingName" type="text" class="form-control" placeholder="Enter Building Name">
              </div>
            </div>

            <div class="col-12">
              <div class="bg-primary border-2 bg-soft p-3 mb-4">
                <p class="fw-semibold mb-0 pb-0">Contact Person</p>
              </div>
            </div>

            <div class="col-12">
              <div class="mb-3">
                <label class="form-label">Contact Person's Full names</label>
                <input v-model="localTrade.businessDetails.contactPersonNames" type="text" class="form-control" placeholder="Enter full Names">
              </div>
            </div>

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">Contact Person's ID No.</label>
                <input v-model="localTrade.businessDetails.contactPersonIDNo" type="text" class="form-control" placeholder="Enter ID No.">
              </div>
            </div>

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">Person's Role</label>
                <select v-model="localTrade.businessDetails.businessRole" class="form-control bg-white">
                  <option v-for="(item, index) in roles" :value="item.role" :key="index">{{ item.role }}</option>
                </select>
              </div>
            </div>

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">Contact Person's Email address</label>
                <input v-model="localTrade.businessDetails.contactPersonEmail" type="text" class="form-control" placeholder="ie email@email.com">
              </div>
            </div>

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">Contact Person's mobile number</label>
                <input v-model="localTrade.businessDetails.contactPersonPhone" type="text" class="form-control" placeholder="ie 07....">
              </div>
            </div>
          </div>

          <!-- Step 4: Business Activity -->
          <div class="row the-step biz-activity d-none">
            <div class="col-12">
              <div class="bg-primary border-2 bg-soft p-3 mb-4">
                <p class="fw-semibold mb-0 pb-0">Business Activity information</p>
              </div>
            </div>

            <div class="col-12">
              <div class="mb-3">
                <label class="form-label">Business Categories</label>
                <select @change="$emit('category-changed', $event)" class="form-control bg-white">
                  <option value="">Select Options</option>
                  <option v-for="(item, index) in tradeCategories" :value="item.incomeTypeId" :key="index">{{ item.incomeTypeDescription }}</option>
                </select>
              </div>
            </div>

            <div class="col-12">
              <div class="mb-3">
                <label class="form-label">Business establishment Type</label>
                <select @change="handleSubcategoryChange($event)" class="form-control bg-white">
                  <option v-for="(item, index) in tradeSubCategories" :value="JSON.stringify(item)" :key="index">{{ item.feeDescription }} KES {{ numberFormat(item.unitFeeAmount) }}</option>
                </select>
              </div>
            </div>

            <div class="col-6">
              <div class="mb-3">
                <label class="form-label">Activity Description <span class="text-danger fw-bold">*</span></label>
                <input v-model="localTrade.businessDetails.businessDes" type="text" class="form-control" placeholder="Eg retail shop for general goods">
              </div>
            </div>

            <div class="col-6">
              <div class="mb-3">
                <label class="form-label">Premises Size (M<sup>2</sup>)</label>
                <input v-model="localTrade.businessDetails.premiseSize" type="text" class="form-control" placeholder="Enter Premises Size">
              </div>
            </div>

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">Vehicle Tonnage</label>
                <select v-model="localTrade.businessDetails.tonnage" class="form-control bg-white">
                  <option v-for="(item, index) in tonnageOptions" :value="item.tonnage" :key="index">{{ item.tonnage }}</option>
                </select>
              </div>
            </div>

            <div class="col-4">
              <div class="mb-3">
                <label class="form-label">Number Of Employees</label>
                <input v-model="localTrade.businessDetails.numberOfEmployees" type="text" class="form-control" placeholder="Enter Number Of Employees">
              </div>
            </div>

            <!-- Other Applicable Charges Section -->
            <div class="col-12">
              <div class="bg-primary border-2 bg-soft p-3 mb-3">
                <p class="fw-semibold mb-0 pb-0">Other Applicable Charges</p>
              </div>
            </div>

            <div class="col-12">
              <div class="mb-3">
                <label class="form-label fw-semibold"><span class="badge bg-primary">Step 1</span> Select Charge Category</label>
                <div class="d-flex gap-2">
                  <select v-model="selectedOtherCategory" class="form-control" @change="handleCategoryChange">
                    <option :value="null">-- Select a category --</option>
                    <option v-for="(category, index) in otherChargeCategories" :key="index" :value="category">{{ category.incomeTypeDescription }}</option>
                  </select>
                  <button type="button" class="btn btn-outline-primary d-flex align-items-center gap-1" @click="$emit('toggle-charges')" style="white-space: nowrap; min-width: 100px;">
                    <i class="bx bx-refresh"></i><span>Toggle</span>
                  </button>
                </div>
                <div class="d-flex align-items-center gap-2 mt-1">
                  <span class="badge" :class="showAllCharges ? 'bg-info' : 'bg-success'">{{ showAllCharges ? 'Showing all categories' : 'Showing filtered categories' }}</span>
                  <small class="text-muted">({{ otherChargeCategories.length }} available)</small>
                </div>
              </div>
            </div>

            <!-- Flat-list mode: when no category is picked yet but the parent
                 has eagerly pre-loaded all "Other" fees, let the officer pick
                 directly from a single dropdown — matches the TopUpModal /
                 Android Enforcement-craft experience. -->
            <div class="col-7" v-if="!selectedOtherCategory && tradeAllOtherFees.length > 0">
              <div class="mb-3">
                <label class="form-label fw-semibold">
                  <span class="badge bg-success">Quick Pick</span>
                  Select a charge directly
                </label>
                <select v-model="selectedCharge" class="form-control">
                  <option :value="null">-- Pick any charge --</option>
                  <option v-for="(charge, index) in tradeAllOtherFees" :key="'flat-' + index" :value="charge">
                    {{ charge.feeDescription }}
                    <span v-if="charge._incomeTypeDescription"> · {{ charge._incomeTypeDescription }}</span>
                    — KES {{ numberFormat(charge.unitFeeAmount) }}
                  </option>
                </select>
                <small class="text-muted">{{ tradeAllOtherFees.length }} fees pre-loaded across {{ otherChargeCategories.length }} categories</small>
              </div>
            </div>

            <!-- Two-step mode (still works): if a category is picked, show its specific charges -->
            <div class="col-7" v-if="selectedOtherCategory && tradeSubCategoriesOther.length > 0">
              <div class="mb-3">
                <label class="form-label fw-semibold"><span class="badge bg-success">Step 2</span> Select Specific Charge</label>
                <select v-model="selectedCharge" class="form-control">
                  <option :value="null">-- Select a charge to add --</option>
                  <option v-for="(charge, index) in tradeSubCategoriesOther" :key="index" :value="charge">{{ charge.feeDescription }} - KES {{ numberFormat(charge.unitFeeAmount) }}</option>
                </select>
              </div>
            </div>

            <!-- Quantity + Add — visible whenever a charge has been picked, regardless of mode -->
            <div class="col-3" v-if="(selectedOtherCategory && tradeSubCategoriesOther.length > 0) || (!selectedOtherCategory && tradeAllOtherFees.length > 0)">
              <div class="mb-3">
                <label class="form-label fw-semibold"><span class="badge bg-info">Quantity</span></label>
                <input v-model.number="chargeQuantity" type="number" min="1" class="form-control" placeholder="1">
              </div>
            </div>

            <div class="col-2" v-if="(selectedOtherCategory && tradeSubCategoriesOther.length > 0) || (!selectedOtherCategory && tradeAllOtherFees.length > 0)">
              <div class="mb-3">
                <label class="form-label fw-semibold">&nbsp;</label>
                <button type="button" class="btn btn-success w-100" @click="addChargeToList" :disabled="!selectedCharge">
                  <i class="bx bx-plus-circle"></i> Add
                </button>
              </div>
            </div>

            <div class="col-12" v-if="selectedOtherCategory && tradeSubCategoriesOther.length === 0">
              <div class="alert alert-warning"><i class="bx bx-loader bx-spin"></i> Loading charges for {{ selectedOtherCategory.incomeTypeDescription }}...</div>
            </div>

            <div class="col-12" v-if="addedCharges.length > 0">
              <div class="table-responsive">
                <table class="table table-nowrap vertical-align-middle table-bordered table-sm">
                  <thead class="table-success">
                  <tr class="text-uppercase">
                    <th>Charge Description</th>
                    <th width="100" class="text-center">Quantity</th>
                    <th width="130" class="text-end">Unit Price</th>
                    <th width="130" class="text-end">Total</th>
                    <th width="80" class="text-center">Action</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(item, index) in addedCharges" :key="index">
                    <td>{{ item.feeDescription }}</td>
                    <td class="text-center">{{ item.quantity }}</td>
                    <td class="text-end">KES {{ numberFormat(item.unitFeeAmount) }}</td>
                    <td class="text-end fw-bold">KES {{ numberFormat(item.unitFeeAmount * item.quantity) }}</td>
                    <td class="text-center">
                      <button type="button" class="btn btn-sm btn-danger" @click="removeCharge(index)"><i class="bx bx-trash"></i></button>
                    </td>
                  </tr>
                  </tbody>
                  <tfoot class="table-info">
                  <tr>
                    <th colspan="3" class="text-end text-uppercase">Grand Total:</th>
                    <th class="text-end fw-bold">KES {{ numberFormat(totalAddedCharges) }}</th>
                    <th></th>
                  </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div class="col-12" v-if="addedCharges.length === 0">
              <div class="alert alert-info"><i class="bx bx-info-circle"></i> <strong>No charges added yet.</strong> Select a category and charge above, then click "Add" to include it.</div>
            </div>

            <div class="col-12">
              <div class="table-responsive">
                <table class="table table-nowrap vertical-align-middle table-bordered table-sm">
                  <thead class="table-info">
                  <tr class="text-uppercase table-info"><th>Business Penalties <i>(-- Select Where Applicable. --)</i></th></tr>
                  </thead>
                  <tbody>
                  <tr>
                    <td>
                      <div class="form-check form-check-primary mb-0">
                        <input class="form-check-input" type="checkbox" :checked="penalized" @change="$emit('penalty-changed', $event.target.checked)">
                        <label class="form-check-label">Penalty</label>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                  <tfoot class="table-success"><tr><th class="text-uppercase">Penalty Charges</th></tr></tfoot>
                </table>
              </div>
            </div>

            <div class="col-12 mt-4">
              <div class="bg-warning border-2 bg-soft p-3 mb-4">
                <p class="mb-0 pb-0 fw-semibold">Computed Amount</p>
                <div class="d-flex justify-content-between align-items-center mt-2">
                  <div>
                    <small class="text-muted d-block">Base Permit Fee:</small>
                    <small class="text-muted d-block">Other Charges:</small>
                  </div>
                  <div class="text-end">
                    <small class="d-block">KES {{ numberFormat(localTrade.amount) }}</small>
                    <small class="d-block">KES {{ numberFormat(totalAddedCharges) }}</small>
                  </div>
                </div>
                <hr class="my-2">
                <h4 class="fw-bold mb-0 pb-0">KES {{ numberFormat(totalComputedAmount) }}</h4>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <div class="d-flex modal-next-prev-container">
            <button
                type="button"
                class="btn btn-outline-secondary btn-prev waves-effect waves-light me-3 d-none"
                @click.stop.prevent="handlePrevClick"
            >
              <i class="bx bx-left-arrow-alt font-size-16 align-middle me-2"></i> Previous
            </button>

            <button
                type="button"
                class="btn btn-primary waves-effect waves-light btn-next"
                :disabled="isNextDisabled"
                @click.stop.prevent="handleNextClick"
            >
              Next <i class="bx bx-right-arrow-alt font-size-16 align-middle me-2"></i>
            </button>

            <button
                @click.stop.prevent="handleSubmit"
                type="button"
                class="btn btn-submit btn-success d-none ml-3 ms-3 waves-effect waves-light"
            >
              <i class="bx bx-check-double font-size-16 align-middle me-2"></i> Submit Application
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  name: 'NewBusinessModal',

  props: {
    trade: { type: Object, required: true },
    subCounties: { type: Array, default: () => [] },
    wards: { type: Array, default: () => [] },
    floors: { type: Array, default: () => [] },
    roles: { type: Array, default: () => [] },
    years: { type: Array, default: () => [] },
    tradeCategories: { type: Array, default: () => [] },
    tradeSubCategories: { type: Array, default: () => [] },
    tradeSubCategoriesOther: { type: Array, default: () => [] },
    otherChargeCategories: { type: Array, default: () => [] },
    // Pre-loaded flat list of every fee across every "Other" income type.
    // When supplied (and no category is picked yet), the modal lets the
    // officer pick any fee directly from a single dropdown — same flow as
    // the Android Enforcement-craft app's flat picker.
    tradeAllOtherFees: { type: Array, default: () => [] },
    tonnageOptions: { type: Array, default: () => [] },
    totalFeeAmountOthers: { type: Number, default: 0 },
    penalized: { type: Boolean, default: false },
    showAllCharges: { type: Boolean, default: false },
    message: { type: String, default: null }
  },

  data() {
    return {
      localTrade: JSON.parse(JSON.stringify(this.trade)),
      localSubCounty: null,
      localWard: null,
      selectedOtherCategory: null,
      selectedCharge: null,
      chargeQuantity: 1,
      addedCharges: [],
      map: null,
      marker: null,
      mapInitializing: false,
      useCurrentLocation: false,
      gettingLocation: false,
      locationError: null,
      locationSuccess: false,
      currentStep: 0,
      permitStartDate: '',
      permitEndDate: '',
      yearConfirmed: false,
      businessNameError: false, // NEW: Track business name validation error
      // Location-related variables
      locationAddress: '',
      fetchingAddress: false,
      searchQuery: '',
      searchResults: [],
      searching: false,
      showResults: false,
      highlightedIndex: -1,
      searchDebounceTimer: null,
      // Step names for indicator
      stepNames: ['Year', 'Owner', 'Location', 'Contact', 'Activity']
    }
  },

  computed: {
    totalAddedCharges() {
      return this.addedCharges.reduce((total, item) => total + (item.unitFeeAmount * item.quantity), 0);
    },
    totalComputedAmount() {
      return (parseFloat(this.localTrade.amount) || 0) + this.totalAddedCharges;
    },
    currentYear() { return new Date().getFullYear(); },
    nextYear() { return new Date().getFullYear() + 1; },

    // Check if location has been captured (not default Nairobi coordinates)
    locationCaptured() {
      const lat = this.localTrade.businessDetails.lat;
      const lng = this.localTrade.businessDetails.lng;
      const isDefaultNairobi = (lat === -1.286389 && lng === 36.817223);
      return lat && lng && lat !== 0 && lng !== 0 && !isDefaultNairobi;
    },

    // Determine if Next button should be disabled based on current step
    isNextDisabled() {
      if (this.currentStep === 0) {
        return !this.localTrade.fiscalYear || !this.yearConfirmed;
      }
      if (this.currentStep === 2) {
        // Check both business name and location
        const hasBusinessName = this.localTrade.businessDetails.businessName && this.localTrade.businessDetails.businessName.trim() !== '';
        return !hasBusinessName || !this.locationCaptured;
      }
      return false;
    }
  },

  watch: {
    trade: {
      handler(newVal) {
        if (!this.localTrade.fiscalYear && newVal.fiscalYear) {
          this.localTrade = JSON.parse(JSON.stringify(newVal))
        } else {
          this.localTrade = {
            ...JSON.parse(JSON.stringify(newVal)),
            fiscalYear: this.localTrade.fiscalYear || newVal.fiscalYear,
            businessDetails: { ...newVal.businessDetails, ...this.localTrade.businessDetails }
          }
        }
      },
      deep: true
    },
    'trade.businessDetails.subCountyID': function(newVal, oldVal) {
      if (newVal && newVal !== oldVal && newVal !== this.localTrade.businessDetails.subCountyID) {
        this.localTrade.businessDetails.subCountyID = this.trade.businessDetails.subCountyID;
        this.localTrade.businessDetails.subCountyName = this.trade.businessDetails.subCountyName;
        const subCounty = this.subCounties.find(sc => sc.subCountyID === newVal);
        if (subCounty) this.localSubCounty = subCounty;
      }
    },
    'trade.businessDetails.wardID': function(newVal, oldVal) {
      if (newVal && newVal !== oldVal && newVal !== this.localTrade.businessDetails.wardID) {
        this.localTrade.businessDetails.wardID = this.trade.businessDetails.wardID;
        this.localTrade.businessDetails.wardName = this.trade.businessDetails.wardName;
        const ward = this.wards.find(w => w.wardID === newVal);
        if (ward) this.localWard = ward;
      }
    },
    localSubCounty(newVal) {
      if (newVal) {
        this.localTrade.businessDetails.subCountyName = newVal.subCountyName;
        this.localTrade.businessDetails.subCountyID = newVal.subCountyID;
        this.$emit('subcounty-changed', newVal);
      }
    },
    localWard(newVal) {
      if (newVal) {
        this.localTrade.businessDetails.wardName = newVal.wardName;
        this.localTrade.businessDetails.wardID = newVal.wardID;
        this.$emit('ward-changed', newVal);
      }
    },
    'localTrade.fiscalYear': function(newVal, oldVal) {
      if (newVal) {
        this.updatePermitDates();
        if (oldVal && newVal !== oldVal) this.yearConfirmed = false;
      } else {
        this.yearConfirmed = false;
      }
    },
    // NEW: Watch business name to clear error when user types
    'localTrade.businessDetails.businessName': function(newVal) {
      if (newVal && newVal.trim() !== '') {
        this.businessNameError = false;
      }
    },
    // NEW: Watch physicalAddress to auto-remove apostrophes
    'localTrade.businessDetails.physicalAddress': function(newVal) {
      if (newVal && typeof newVal === 'string') {
        const sanitized = this.sanitizeAddress(newVal);
        if (sanitized !== newVal) {
          this.localTrade.businessDetails.physicalAddress = sanitized;
        }
      }
    }
  },

  mounted() {
    const modalElement = document.getElementById('new-business');
    if (modalElement) {
      // Initialize map when modal is shown
      modalElement.addEventListener('shown.bs.modal', () => {
        console.log('Modal shown - initializing map');
        setTimeout(() => this.initMapWithRetry(), 300);
      });

      if (window.$ && window.$.fn.modal) {
        $('#new-business').on('shown.bs.modal', () => {
          console.log('Modal shown (jQuery) - initializing map');
          setTimeout(() => this.initMapWithRetry(), 300);
        });
      }

      // Fallback initialization
      setTimeout(() => {
        if (modalElement.classList.contains('show') && !this.map) {
          this.initMapWithRetry();
        }
      }, 500);

      setTimeout(() => {
        if (modalElement.classList.contains('show') && !this.map) {
          this.initMapWithRetry();
        }
      }, 2000);
    }

    // Click outside handler to close search results
    document.addEventListener('click', this.handleClickOutside);

    // Log initial state
    console.log('Component mounted - currentStep:', this.currentStep);
  },

  beforeUnmount() {
    const modalElement = document.getElementById('new-business');
    if (modalElement) {
      modalElement.removeEventListener('shown.bs.modal', this.initLeafletMap);
    }

    // Remove click outside handler
    document.removeEventListener('click', this.handleClickOutside);

    // Clear search debounce timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    // Clean up Leaflet map
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
    this.marker = null;
  },

  methods: {
    // NEW: Sanitize address to remove apostrophes and other problematic characters
    sanitizeAddress(address) {
      if (!address || typeof address !== 'string') return address;

      // Remove apostrophes (both straight and curly)
      // Also remove other characters that might cause SQL issues
      return address
          .replace(/['′'`]/g, '')  // Remove various types of apostrophes
          .replace(/["″"]/g, '')   // Remove various types of quotes
          .replace(/\\/g, '')      // Remove backslashes
          .trim();
    },

    updatePermitDates() {
      if (this.localTrade.fiscalYear) {
        const year = parseInt(this.localTrade.fiscalYear);
        this.permitStartDate = `1st January ${year}`;
        this.permitEndDate = `31st December ${year}`;
      } else {
        this.permitStartDate = '';
        this.permitEndDate = '';
      }
    },

    handleNextClick() {
      // First, sync currentStep from DOM to ensure accuracy
      this.syncCurrentStepFromDOM();

      console.log('=== handleNextClick === currentStep:', this.currentStep);

      // Validation for Year Selection step (Step 0)
      if (this.currentStep === 0 && !this.localTrade.fiscalYear) {
        this.$emit('update:message', 'Please select a permit year before proceeding');
        return;
      }
      if (this.currentStep === 0 && !this.yearConfirmed) {
        this.$emit('update:message', 'Please confirm that you understand the permit validity period before proceeding');
        return;
      }

      // NEW: Validation for Business Details step (Step 2) - Business Name AND Location required
      if (this.currentStep === 2) {
        // Check Business Name first
        if (!this.localTrade.businessDetails.businessName || this.localTrade.businessDetails.businessName.trim() === '') {
          this.businessNameError = true;
          this.$emit('update:message', 'Please enter the Business Name before proceeding');
          return;
        }
        this.businessNameError = false;

        // Then check location
        if (!this.locationCaptured) {
          this.$emit('update:message', 'Please provide the business location. Use GPS coordinates or select a location on the map.');
          return;
        }
      }

      this.$emit('update:message', null);

      // Navigate to next step
      const nextStep = this.currentStep + 1;
      console.log('Going to next step:', nextStep);
      this.goToStep(nextStep);
    },

    handlePrevClick() {
      // First, sync currentStep from DOM to ensure accuracy
      this.syncCurrentStepFromDOM();

      console.log('=== handlePrevClick === currentStep:', this.currentStep);

      if (this.currentStep > 0) {
        const prevStep = this.currentStep - 1;
        console.log('Going to previous step:', prevStep);
        this.goToStep(prevStep);
      }
    },

    // Sync currentStep from DOM - finds which step is currently visible
    syncCurrentStepFromDOM() {
      const steps = document.querySelectorAll('#new-business .the-step');
      let foundIndex = 0;

      steps.forEach((step, index) => {
        if (!step.classList.contains('d-none')) {
          foundIndex = index;
        }
      });

      if (this.currentStep !== foundIndex) {
        console.log('Syncing currentStep from', this.currentStep, 'to', foundIndex);
        this.currentStep = foundIndex;
      }
    },

    goToStep(targetStep) {
      const steps = document.querySelectorAll('#new-business .the-step');
      const prevBtn = document.querySelector('#new-business .btn-prev');
      const nextBtn = document.querySelector('#new-business .btn-next');
      const submitBtn = document.querySelector('#new-business .btn-submit');

      const totalSteps = steps.length;

      console.log('=== goToStep ===');
      console.log('Target step:', targetStep, '| Total steps:', totalSteps);

      // Validate target step
      if (targetStep < 0 || targetStep >= totalSteps) {
        console.log('ERROR: Invalid target step', targetStep, '- must be between 0 and', totalSteps - 1);
        return;
      }

      // Log current state before changes
      console.log('Steps before change:');
      steps.forEach((step, index) => {
        const isVisible = !step.classList.contains('d-none');
        if (isVisible) console.log('  Step', index, 'is VISIBLE');
      });

      // Hide ALL steps first
      for (let i = 0; i < steps.length; i++) {
        steps[i].classList.add('d-none');
        steps[i].classList.remove('active');
      }

      // Show ONLY the target step
      steps[targetStep].classList.remove('d-none');
      steps[targetStep].classList.add('active');

      // Update current step tracker
      this.currentStep = targetStep;

      // Log state after changes
      console.log('Steps after change:');
      steps.forEach((step, index) => {
        const isVisible = !step.classList.contains('d-none');
        if (isVisible) console.log('  Step', index, 'is VISIBLE');
      });

      // === BUTTON VISIBILITY LOGIC ===

      // Previous button: hidden on step 0, visible on all other steps
      if (prevBtn) {
        if (targetStep === 0) {
          prevBtn.classList.add('d-none');
          console.log('Previous button: HIDDEN (step 0)');
        } else {
          prevBtn.classList.remove('d-none');
          console.log('Previous button: VISIBLE (step', targetStep, ')');
        }
      }

      // Next/Submit button logic
      const isLastStep = (targetStep === totalSteps - 1);

      if (isLastStep) {
        // Last step: hide Next, show Submit
        if (nextBtn) nextBtn.classList.add('d-none');
        if (submitBtn) submitBtn.classList.remove('d-none');
        console.log('Last step - Next: HIDDEN, Submit: VISIBLE');
      } else {
        // Not last step: show Next, hide Submit
        if (nextBtn) nextBtn.classList.remove('d-none');
        if (submitBtn) submitBtn.classList.add('d-none');
        console.log('Not last step - Next: VISIBLE, Submit: HIDDEN');
      }

      // Re-initialize map if we're on the business details step (step 2)
      if (targetStep === 2) {
        setTimeout(() => {
          this.initMapWithRetry();
        }, 300);
      }

      console.log('=== Navigation complete. Now on step:', this.currentStep, '===');
    },

    clearLocation() {
      this.localTrade.businessDetails.lat = 0;
      this.localTrade.businessDetails.lng = 0;
      this.useCurrentLocation = false;
      this.locationSuccess = false;
      this.locationError = null;
      this.locationAddress = '';
      this.searchQuery = '';
      this.searchResults = [];

      // Reset Leaflet map and marker
      if (this.map && this.marker) {
        const defaultLat = -1.286389;
        const defaultLng = 36.817223;
        this.map.setView([defaultLat, defaultLng], 13);
        this.marker.setLatLng([defaultLat, defaultLng]);
        this.marker.setPopupContent('<strong>Drag me to set business location</strong><br>Or click on the map').openPopup();
      }
    },

    handleCategoryChange() {
      this.$emit('other-category-selected', this.selectedOtherCategory);
      this.selectedCharge = null;
    },

    handleSubcategoryChange(event) {
      try {
        const data = JSON.parse(event.target.value);
        this.localTrade.businessDetails.businessCategory = data.accountDesc;
        this.localTrade.businessDetails.businessSubCategory = data.feeDescription;
        this.localTrade.businessDetails.feeID = data.feeId;
        this.localTrade.businessDetails.incomeTypeID = data.incomeTypeId;
        this.localTrade.amount = data.unitFeeAmount;
        this.$emit('subcategory-changed', event);
      } catch (error) {
        console.error('Error parsing subcategory:', error);
      }
    },

    addChargeToList() {
      if (!this.selectedCharge) return;
      const existingIndex = this.addedCharges.findIndex(item => item.feeId === this.selectedCharge.feeId);
      if (existingIndex !== -1) {
        this.addedCharges[existingIndex].quantity += this.chargeQuantity;
      } else {
        this.addedCharges.push({ ...this.selectedCharge, quantity: this.chargeQuantity });
      }
      this.$emit('other-charges-updated', this.addedCharges);
      this.selectedCharge = null;
      this.chargeQuantity = 1;
    },

    removeCharge(index) {
      this.addedCharges.splice(index, 1);
      this.$emit('other-charges-updated', this.addedCharges);
    },

    initMapWithRetry(attempts = 0) {
      if (attempts >= 5) return;

      const mapContainer = document.getElementById('leafletMap');
      if (!mapContainer) {
        setTimeout(() => this.initMapWithRetry(attempts + 1), 500);
        return;
      }

      const rect = mapContainer.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        setTimeout(() => this.initMapWithRetry(attempts + 1), 500);
        return;
      }

      this.initLeafletMap();
    },

    initLeafletMap() {
      if (this.map) {
        // Map already exists, just invalidate size
        setTimeout(() => {
          this.map.invalidateSize();
        }, 100);
        return;
      }

      this.mapInitializing = true;

      // Load Leaflet CSS if not already loaded
      if (!document.getElementById('leaflet-css')) {
        const link = document.createElement('link');
        link.id = 'leaflet-css';
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
        link.crossOrigin = '';
        document.head.appendChild(link);
      }

      // Load Leaflet JS if not already loaded
      const initMap = () => {
        const mapContainer = document.getElementById('leafletMap');
        if (!mapContainer) {
          this.mapInitializing = false;
          return;
        }

        try {
          // Default to Nairobi
          const initialLat = -1.286389;
          const initialLng = 36.817223;

          // Create map
          this.map = L.map('leafletMap').setView([initialLat, initialLng], 13);

          // Add OpenStreetMap tile layer
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          }).addTo(this.map);

          // Create draggable marker
          this.marker = L.marker([initialLat, initialLng], {
            draggable: true
          }).addTo(this.map);

          // Add popup to marker
          this.marker.bindPopup('<strong>Drag me to set business location</strong><br>Or click on the map').openPopup();

          // Handle marker drag
          this.marker.on('dragend', (e) => {
            const position = e.target.getLatLng();
            this.updateCoordinates(position.lat, position.lng);
          });

          // Handle map click
          this.map.on('click', (e) => {
            this.marker.setLatLng(e.latlng);
            this.updateCoordinates(e.latlng.lat, e.latlng.lng);
          });

          // Invalidate size after a delay to ensure proper rendering
          setTimeout(() => {
            if (this.map) {
              this.map.invalidateSize();
            }
          }, 300);

          this.mapInitializing = false;

        } catch (error) {
          console.error('Error initializing Leaflet map:', error);
          this.mapInitializing = false;
        }
      };

      // Check if Leaflet is loaded
      if (typeof L !== 'undefined') {
        setTimeout(initMap, 100);
      } else {
        // Load Leaflet JS
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
        script.crossOrigin = '';
        script.onload = () => {
          setTimeout(initMap, 100);
        };
        document.head.appendChild(script);
      }
    },

    updateCoordinates(lat, lng) {
      this.localTrade.businessDetails.lat = lat;
      this.localTrade.businessDetails.lng = lng;

      // Update marker popup
      if (this.marker) {
        this.marker.setPopupContent(
            `<strong>📍 Location Selected</strong><br>
           <span style="color: green;">✓ Coordinates Captured</span><br>
           Lat: ${lat.toFixed(6)}<br>
           Lng: ${lng.toFixed(6)}`
        ).openPopup();
      }

      this.locationSuccess = true;
      this.locationError = null;

      // Perform reverse geocoding
      this.reverseGeocode(lat, lng);

      setTimeout(() => { if (!this.gettingLocation) this.locationSuccess = false; }, 3000);
    },

    // Reverse geocoding using Nominatim (free OpenStreetMap service)
    async reverseGeocode(lat, lng) {
      this.fetchingAddress = true;
      this.locationAddress = '';

      try {
        const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
            {
              headers: {
                'Accept-Language': 'en',
                'User-Agent': 'SBPApplication/1.0'
              }
            }
        );

        if (response.ok) {
          const data = await response.json();
          if (data && data.display_name) {
            // SANITIZE: Remove apostrophes from the address
            const sanitizedAddress = this.sanitizeAddress(data.display_name);
            this.locationAddress = sanitizedAddress;

            // Also store in business details for submission (sanitized)
            this.localTrade.businessDetails.physicalAddress = sanitizedAddress;
          }
        }
      } catch (error) {
        console.error('Reverse geocoding error:', error);
        this.locationAddress = '';
      } finally {
        this.fetchingAddress = false;
      }
    },

    // Debounced search - triggers as user types
    debouncedSearch() {
      // Clear previous timer
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      // Reset highlighted index
      this.highlightedIndex = -1;

      // Don't search if query is too short
      if (!this.searchQuery || this.searchQuery.trim().length < 2) {
        this.searchResults = [];
        this.searching = false;
        return;
      }

      // Show loading state
      this.searching = true;
      this.showResults = true;

      // Debounce the search (300ms delay)
      this.searchDebounceTimer = setTimeout(() => {
        this.performSearch();
      }, 300);
    },

    // Perform the actual search
    async performSearch() {
      if (!this.searchQuery || this.searchQuery.trim().length < 2) {
        this.searchResults = [];
        this.searching = false;
        return;
      }

      try {
        // Add Kenya context to search
        const query = this.searchQuery.toLowerCase().includes('kenya')
            ? this.searchQuery
            : `${this.searchQuery}, Kenya`;

        const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=8&countrycodes=ke&addressdetails=1`,
            {
              headers: {
                'Accept-Language': 'en',
                'User-Agent': 'SBPApplication/1.0'
              }
            }
        );

        if (response.ok) {
          const data = await response.json();
          this.searchResults = data || [];
          this.highlightedIndex = this.searchResults.length > 0 ? 0 : -1;
        }
      } catch (error) {
        console.error('Search error:', error);
        this.searchResults = [];
      } finally {
        this.searching = false;
      }
    },

    // Get place name from result
    getPlaceName(result) {
      if (result.address) {
        // Try to get a meaningful name
        return result.address.amenity ||
            result.address.building ||
            result.address.shop ||
            result.address.office ||
            result.address.road ||
            result.address.neighbourhood ||
            result.address.suburb ||
            result.name ||
            result.display_name.split(',')[0];
      }
      return result.display_name.split(',')[0];
    },

    // Get place details (address without the first part)
    getPlaceDetails(result) {
      const parts = result.display_name.split(',');
      if (parts.length > 1) {
        return parts.slice(1, 4).join(',').trim();
      }
      return '';
    },

    // Navigate through results with keyboard
    navigateResults(direction) {
      if (this.searchResults.length === 0) return;

      if (direction === 'down') {
        this.highlightedIndex = (this.highlightedIndex + 1) % this.searchResults.length;
      } else if (direction === 'up') {
        this.highlightedIndex = this.highlightedIndex <= 0
            ? this.searchResults.length - 1
            : this.highlightedIndex - 1;
      }
    },

    // Select the currently highlighted result
    selectHighlightedResult() {
      if (this.highlightedIndex >= 0 && this.highlightedIndex < this.searchResults.length) {
        this.selectSearchResult(this.searchResults[this.highlightedIndex]);
      }
    },

    // Clear search
    clearSearch() {
      this.searchQuery = '';
      this.searchResults = [];
      this.showResults = false;
      this.highlightedIndex = -1;
      this.searching = false;
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
    },

    // Handle click outside search dropdown
    handleClickOutside(event) {
      const searchContainer = document.querySelector('.search-results-dropdown');
      const searchInput = document.querySelector('input[placeholder*="Start typing to search"]');

      if (searchContainer && searchInput) {
        if (!searchContainer.contains(event.target) && !searchInput.contains(event.target)) {
          this.showResults = false;
        }
      }
    },

    // Select a search result
    selectSearchResult(result) {
      const lat = parseFloat(result.lat);
      const lng = parseFloat(result.lon);

      // Update coordinates
      this.localTrade.businessDetails.lat = lat;
      this.localTrade.businessDetails.lng = lng;

      // SANITIZE: Update address (remove apostrophes)
      const sanitizedAddress = this.sanitizeAddress(result.display_name);
      this.locationAddress = sanitizedAddress;
      this.localTrade.businessDetails.physicalAddress = sanitizedAddress;

      // Move map and marker to selected location
      if (this.map && this.marker) {
        this.map.setView([lat, lng], 17);
        this.marker.setLatLng([lat, lng]);
        this.marker.setPopupContent(
            `<strong>📍 ${this.getPlaceName(result)}</strong><br>
           <small>${this.sanitizeAddress(this.getPlaceDetails(result))}</small><br>
           <span style="color: green;">✓ Location Selected</span>`
        ).openPopup();
      }

      // Clear search
      this.clearSearch();
      this.locationSuccess = true;

      setTimeout(() => { this.locationSuccess = false; }, 3000);
    },

    handleSubmit() {
      if (!this.localTrade.fiscalYear || this.localTrade.fiscalYear === '') {
        this.$emit('update:message', "Please select a fiscal year before submitting");
        return;
      }
      if (!this.localTrade.businessDetails.fullNames || this.localTrade.businessDetails.fullNames.trim() === '') {
        this.$emit('update:message', "Please enter owner's full name");
        return;
      }
      if (!this.localTrade.businessDetails.ownerID || this.localTrade.businessDetails.ownerID.trim() === '') {
        this.$emit('update:message', "Please enter owner's ID number");
        return;
      }
      if (!this.localTrade.businessDetails.ownerPhone || this.localTrade.businessDetails.ownerPhone.trim() === '') {
        this.$emit('update:message', "Please enter owner's phone number");
        return;
      }
      // NEW: Validate business name on submit
      if (!this.localTrade.businessDetails.businessName || this.localTrade.businessDetails.businessName.trim() === '') {
        this.$emit('update:message', "Please enter the business name");
        return;
      }
      if (!this.locationCaptured) {
        this.$emit('update:message', "Please provide the business location coordinates. This is mandatory.");
        return;
      }
      if (!this.localTrade.businessDetails.wardID) {
        this.$emit('update:message', "Please select a ward");
        return;
      }
      if (!this.localTrade.businessDetails.feeID) {
        this.$emit('update:message', "Please select business category and type");
        return;
      }

      // SANITIZE: Final sanitization before submit
      if (this.localTrade.businessDetails.physicalAddress) {
        this.localTrade.businessDetails.physicalAddress = this.sanitizeAddress(this.localTrade.businessDetails.physicalAddress);
      }

      this.$emit('update:message', null);
      this.$emit('register-business', { ...this.localTrade, addedCharges: this.addedCharges });
    },

    handleUseCurrentLocation() {
      if (this.useCurrentLocation) {
        this.getCurrentLocation();
      } else {
        this.locationSuccess = false;
        this.locationError = null;
      }
    },

    getCurrentLocation() {
      if (!navigator.geolocation) {
        this.locationError = 'Geolocation is not supported by your browser. Please select location on the map.';
        this.useCurrentLocation = false;
        return;
      }

      this.gettingLocation = true;
      this.locationError = null;
      this.locationSuccess = false;

      const self = this;

      navigator.geolocation.getCurrentPosition(
          function(position) {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;

            self.localTrade.businessDetails.lat = lat;
            self.localTrade.businessDetails.lng = lng;

            // Move Leaflet map and marker
            if (self.map && self.marker) {
              self.map.setView([lat, lng], 17);
              self.marker.setLatLng([lat, lng]);
              self.marker.setPopupContent(
                  `<strong>📍 Your Current Location</strong><br>
               <span style="color: green;">✓ GPS Coordinates Captured</span><br>
               Lat: ${lat.toFixed(6)}<br>
               Lng: ${lng.toFixed(6)}`
              ).openPopup();
            }

            self.gettingLocation = false;
            self.locationSuccess = true;

            // Perform reverse geocoding
            self.reverseGeocode(lat, lng);
          },
          function(error) {
            console.error('Error getting location:', error);
            switch(error.code) {
              case error.PERMISSION_DENIED:
                self.locationError = "Location access denied. Please enable location permissions in your browser settings, then try again.";
                break;
              case error.POSITION_UNAVAILABLE:
                self.locationError = "Location information unavailable. Please select your location on the map instead.";
                break;
              case error.TIMEOUT:
                self.locationError = "Location request timed out. Please try again or select location on the map.";
                break;
              default:
                self.locationError = "An error occurred while getting location. Please select location on the map.";
            }
            self.gettingLocation = false;
            self.useCurrentLocation = false;
          },
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    },

    resetForm() {
      this.localTrade = JSON.parse(JSON.stringify(this.trade));
      this.localSubCounty = null;
      this.localWard = null;
      this.selectedOtherCategory = null;
      this.selectedCharge = null;
      this.chargeQuantity = 1;
      this.addedCharges = [];
      this.currentStep = 0;
      this.permitStartDate = '';
      this.permitEndDate = '';
      this.yearConfirmed = false;
      this.useCurrentLocation = false;
      this.locationSuccess = false;
      this.locationError = null;
      this.gettingLocation = false;
      this.locationAddress = '';
      this.fetchingAddress = false;
      this.searchQuery = '';
      this.searchResults = [];
      this.searching = false;
      this.showResults = false;
      this.highlightedIndex = -1;
      this.businessNameError = false; // NEW: Reset business name error
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = null;
      }

      // Reset DOM steps to initial state
      const steps = document.querySelectorAll('#new-business .the-step');
      const prevBtn = document.querySelector('#new-business .btn-prev');
      const nextBtn = document.querySelector('#new-business .btn-next');
      const submitBtn = document.querySelector('#new-business .btn-submit');

      steps.forEach((step, index) => {
        if (index === 0) {
          step.classList.remove('d-none');
          step.classList.add('active');
        } else {
          step.classList.add('d-none');
          step.classList.remove('active');
        }
      });

      if (prevBtn) prevBtn.classList.add('d-none');
      if (nextBtn) nextBtn.classList.remove('d-none');
      if (submitBtn) submitBtn.classList.add('d-none');
    },

    numberFormat(num) {
      return new Intl.NumberFormat("en-GB").format(num);
    }
  }
}
</script>

<style scoped>
#leafletMap {
  width: 100%;
  height: 400px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #e5e3df;
  position: relative;
  z-index: 1;
}

.mdi-loading { animation: spin 1s linear infinite; }
@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

.modal-body { max-height: calc(100vh - 280px); overflow-y: auto; }
.permit-validity-card { min-height: 80px; display: flex; flex-direction: column; justify-content: center; }
.year-selection .badge { font-size: 14px; padding: 6px 12px; }
.bg-success-soft { background-color: rgba(40, 167, 69, 0.1) !important; }

/* Step indicator styles */
.step-indicator {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 12px;
}

.step-item {
  position: relative;
}

.step-item:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 15px;
  left: calc(50% + 20px);
  width: calc(100% - 40px);
  height: 2px;
  background-color: #dee2e6;
}

.step-item.completed:not(:last-child)::after {
  background-color: #28a745;
}

.step-circle {
  position: relative;
  z-index: 1;
  transition: all 0.3s ease;
}

/* Search results dropdown */
.search-results-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1050;
  background: white;
  max-height: 350px;
  overflow-y: auto;
  margin-top: 2px;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.search-result-item {
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
}

.search-result-item:hover,
.search-result-item.bg-primary {
  background-color: rgba(13, 110, 253, 0.1) !important;
}

.search-result-item:last-of-type {
  border-bottom: none !important;
}

.search-result-item .fw-semibold {
  font-size: 14px;
  line-height: 1.3;
}

.search-result-item small {
  font-size: 12px;
  line-height: 1.2;
  display: block;
  margin-top: 2px;
}

/* Input group styling */
.input-group .form-control:focus {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
  z-index: 3;
}

.input-group .input-group-text {
  border-right: none;
}

.input-group .form-control {
  border-left: none;
}

.input-group .form-control:focus + .btn,
.input-group .btn:focus {
  z-index: 3;
}

/* Leaflet popup styling */
.leaflet-popup-content-wrapper {
  border-radius: 8px;
}

.leaflet-popup-content {
  margin: 10px;
}

/* Position relative for dropdown container */
.position-relative {
  position: relative !important;
}
</style>